import area from "@turf/area";
import kinks from "@turf/kinks";
import { polygon as turfPolygon } from "@turf/helpers";

/**
 * Client-side geometric validation for the `points` (Polygon) field of Areas
 * and Discount Areas. Mirrors the strict backend rules so an invalid polygon
 * never reaches POST/PUT `/areas` or `/discount-areas`.
 *
 * Each rule maps to a dedicated i18n key so the user gets a precise hint
 * (never a single generic "invalid location" message), matching the backend
 * `errors.points` contract.
 */
export type PolygonValidationErrorKey =
  | "polygon_min_points"
  | "polygon_out_of_range"
  | "polygon_duplicate_points"
  | "polygon_self_intersects"
  | "polygon_zero_area";

export interface PolygonValidationResult {
  /** true only when a polygon exists and violates no rule. */
  valid: boolean;
  /** i18n key of the first violated rule, or null when valid / nothing drawn. */
  errorKey: PolygonValidationErrorKey | null;
  /** Surface area in square meters (0 when not computable). */
  area: number;
  /** Number of distinct corners, excluding the closing point. */
  cornerCount: number;
  /** Whether any polygon has been drawn at all. */
  hasPolygon: boolean;
}

const EMPTY_RESULT: PolygonValidationResult = {
  valid: false,
  errorKey: null,
  area: 0,
  cornerCount: 0,
  hasPolygon: false,
};

/**
 * Minimum surface (m²) below which a polygon is treated as having "no area"
 * (collinear points / degenerate sliver). Real delivery zones are far larger,
 * while a line collapses to ~0, so this cleanly separates the two.
 */
const ZERO_AREA_EPSILON_M2 = 1;

const samePoint = (a?: number[], b?: number[]): boolean =>
  !!a && !!b && a[0] === b[0] && a[1] === b[1];

/**
 * Validate the outer ring of a GeoJSON Polygon. Coordinates are
 * `[ [ [lng, lat], ... ] ]` (longitude first, matching the backend axis order).
 * The ring may be open or closed; it is closed internally for area/kinks.
 */
export const validatePolygonCoordinates = (
  coordinates?: number[][][] | null,
): PolygonValidationResult => {
  const ring = coordinates?.[0];
  if (!Array.isArray(ring) || ring.length === 0) {
    return EMPTY_RESULT;
  }

  // Work with the ring without its (optional) closing point.
  const isClosed = ring.length > 1 && samePoint(ring[0], ring[ring.length - 1]);
  const openRing = isClosed ? ring.slice(0, -1) : ring;
  const cornerCount = openRing.length;

  const base = { hasPolygon: true, area: 0, cornerCount };

  // 1) out_of_range — also guards turf against non-finite input.
  for (const position of openRing) {
    const lng = position?.[0];
    const lat = position?.[1];
    if (
      !Number.isFinite(lng) ||
      !Number.isFinite(lat) ||
      lng < -180 ||
      lng > 180 ||
      lat < -90 ||
      lat > 90
    ) {
      return { ...base, valid: false, errorKey: "polygon_out_of_range" };
    }
  }

  // 2) min_points — at least 3 distinct corners (the closing point is extra).
  if (cornerCount < 3) {
    return { ...base, valid: false, errorKey: "polygon_min_points" };
  }

  // 3) duplicate_points — consecutive identical corners.
  for (let i = 1; i < openRing.length; i++) {
    if (samePoint(openRing[i], openRing[i - 1])) {
      return { ...base, valid: false, errorKey: "polygon_duplicate_points" };
    }
  }

  // Build a closed linear ring for turf (it requires first === last).
  const closedRing = [...openRing, openRing[0]];

  let computedArea = 0;
  let hasKinks = false;
  try {
    const feature = turfPolygon([closedRing]);
    computedArea = area(feature);
    hasKinks = (kinks(feature)?.features?.length ?? 0) > 0;
  } catch {
    // turf throws on degenerate geometry -> no usable area.
    return { ...base, valid: false, errorKey: "polygon_zero_area" };
  }

  // 4) self_intersects — butterfly / figure-8 shapes.
  if (hasKinks) {
    return {
      ...base,
      area: computedArea,
      valid: false,
      errorKey: "polygon_self_intersects",
    };
  }

  // 5) zero_area — collinear points / no surface.
  if (!(computedArea > ZERO_AREA_EPSILON_M2)) {
    return {
      ...base,
      area: computedArea,
      valid: false,
      errorKey: "polygon_zero_area",
    };
  }

  return { ...base, area: computedArea, valid: true, errorKey: null };
};

/** Human-readable area label, e.g. "12,345 m²" or "1.23 km²". */
export const formatPolygonArea = (squareMeters: number): string => {
  if (!squareMeters || squareMeters <= 0) return "0 m²";
  if (squareMeters >= 1_000_000) {
    const km2 = squareMeters / 1_000_000;
    return `${km2.toLocaleString(undefined, { maximumFractionDigits: 2 })} km²`;
  }
  return `${Math.round(squareMeters).toLocaleString()} m²`;
};
